import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Subscription } from "rxjs";
import {
  AVAILABLE_LOCALES,
  AvailableLocale,
} from "src/app/_static-data/available-locale.library";
import {
  NewPublication,
  Publication,
  PublicationTranslation,
  PublicationTypeWithRelations,
  PublicationWithRelations,
} from "src/app/api/models";
import { TinyService } from "src/app/shared/services/tiny.service";

export interface PublicationCreateUpdateDialogData {
  context: "editor" | "inventory" | "item";
  publication?: PublicationWithRelations;
  types: PublicationTypeWithRelations[];
}

@Component({
  selector: "app-publication-create-update-dialog",
  templateUrl: "./publication-create-update-dialog.component.html",
  styleUrl: "./publication-create-update-dialog.component.scss",
  standalone: false,
})
export class PublicationCreateUpdateDialogComponent
  implements OnInit, OnDestroy
{
  availableLocales: AvailableLocale[] = AVAILABLE_LOCALES;
  TITLE_CONTROL_NAMES: string[] = ["originalTitle", "titleCS", "titleEN"];

  form: UntypedFormGroup;
  mode: "create" | "update" = "create";
  context: "editor" | "inventory" | "item";
  publication: PublicationWithRelations;
  publicationTypes: PublicationTypeWithRelations[];

  originalTitleLocaleSubscription: Subscription = new Subscription();
  titleEnSubscription: Subscription = new Subscription();
  titleCsSubscription: Subscription = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA) public defaults: PublicationCreateUpdateDialogData,
    private dialogRef: MatDialogRef<PublicationCreateUpdateDialogComponent>,
    private fb: UntypedFormBuilder,
    public tinyService: TinyService
  ) {}
  ngOnDestroy(): void {
    this.originalTitleLocaleSubscription.unsubscribe();
    this.titleEnSubscription.unsubscribe();
    this.titleCsSubscription.unsubscribe();
  }

  async ngOnInit(): Promise<void> {
    this.publicationTypes = this.defaults.types ?? [];
    this.context = this.defaults.context;
    if (this.defaults.publication) {
      this.mode = "update";
      this.publication = this.defaults.publication;
    } else {
      this.publication = {} as PublicationWithRelations;
    }
    this.prepareForm();
    if (!this.canEdit()) {
      this.form.disable({ emitEvent: false });
    }
  }

  /** Default submit function */
  save() {
    if (this.mode === "create") {
      this.create();
    } else if (this.mode === "update") {
      this.update();
    }
  }

  /** CRUD Functions */
  private create() {
    const np: NewPublication = {
      publication: {
        originalTitle: this.getLocalizedOriginalTitle(),
        publicationTypeId: this.form.value["publicationTypeId"],
        author: this.form.value["author"],
        year: this.form.value["year"],
        isbn: this.form.value["isbn"] === "" ? null : this.form.value["isbn"],
        curated: this.form.value["curated"],
      },
      translations: [
        {
          localeId: 1,
          title: this.form.value["titleCS"],
          description: this.form.value["descriptionCS"],
          isOriginalTitle:
            this.form.value["originalTitleLocale"] === "cs" ? true : false,
          year: this.form.value["yearCS"],
        },
        {
          localeId: 2,
          title: this.form.value["titleEN"],
          description: this.form.value["descriptionEN"],
          isOriginalTitle:
            this.form.value["originalTitleLocale"] === "en" ? true : false,
          year: this.form.value["yearEN"],
        },
      ],
    };
    this.dialogRef.close(np);
  }

  private update() {
    const tCS = this.publication.translations?.find((a) => a.localeId === 1);
    const tEN = this.publication.translations?.find((a) => a.localeId === 2);

    //FIXME:Make OriginalTitle nor required
    const publication: Partial<Publication> = {
      id: this.publication.id,
      originalTitle: this.getLocalizedOriginalTitle(),
      author: this.form.value["author"],
      year: this.form.value["year"],
      isbn: this.form.value["isbn"] === "" ? null : this.form.value["isbn"],
      publicationTypeId: Number(this.form.value["publicationTypeId"]),
      curated: this.form.value["curated"],
    };

    const translationCS: PublicationTranslation = {
      id: tCS.id,
      localeId: 1,
      title: this.form.value["titleCS"],
      description: this.form.value["descriptionCS"],
      year: this.form.value["yearCS"],
    };

    //Updates info about original title locale if was changed
    if (
      this.form.value["originalTitleLocale"] !== "cs" &&
      tCS.isOriginalTitle === true
    ) {
      translationCS.isOriginalTitle = false;
    } else if (
      this.form.value["originalTitleLocale"] === "cs" &&
      tCS.isOriginalTitle === false
    ) {
      translationCS.isOriginalTitle = true;
    }

    const translationEN: PublicationTranslation = {
      id: tEN.id,
      localeId: 2,
      title: this.form.value["titleEN"],
      description: this.form.value["descriptionEN"],
      year: this.form.value["yearEN"],
    };

    //Updates info about original title locale if was changed
    if (
      this.form.value["originalTitleLocale"] !== "en" &&
      tEN.isOriginalTitle === true
    ) {
      translationEN.isOriginalTitle = false;
    } else if (
      this.form.value["originalTitleLocale"] === "en" &&
      tEN.isOriginalTitle === false
    ) {
      translationEN.isOriginalTitle = true;
    }

    const translations: PublicationTranslation[] = [
      translationCS,
      translationEN,
    ];
    this.dialogRef.close({ publication, translations });
  }

  /* ---------------------------------------------------------
  DIALOG CONTENT METHODS
  --------------------------------------------------------- */
  isCreateMode() {
    return this.mode === "create";
  }

  isUpdateMode() {
    return this.mode === "update";
  }

  isEditorContext() {
    return this.context === "editor";
  }

  isInventoryContext() {
    return this.context === "inventory";
  }

  canEdit() {
    const isLocalPublication = this.defaults.publication?.inventoryId;
    return (
      this.isEditorContext() ||
      (this.isUpdateMode() &&
        isLocalPublication &&
        this.isInventoryContext()) ||
      this.isCreateMode()
    );
  }

  private prepareForm() {
    const translationCS = this.publication.translations?.find(
      (a) => a.localeId === 1
    );
    const translationEN = this.publication.translations?.find(
      (a) => a.localeId === 2
    );

    let originalTitleLocale: "other" | "cs" | "en" = "cs";

    if (translationCS?.isOriginalTitle === true) {
      originalTitleLocale = "cs";
    } else if (translationEN?.isOriginalTitle === true) {
      originalTitleLocale = "en";
    } else if (!translationCS && !translationEN) {
      originalTitleLocale = "cs";
    } else {
      originalTitleLocale = "other";
    }

    this.form = this.fb.group({
      originalTitle: [this.publication.originalTitle || ""],
      originalTitleLocale: [originalTitleLocale],
      author: [this.publication.author || ""],
      isbn: [this.publication.isbn || ""],
      publicationTypeId: [
        this.publication.publicationTypeId || 1,
        Validators.required,
      ],
      titleCS: [translationCS?.title || ""],
      titleEN: [translationEN?.title || ""],
      descriptionCS: [translationCS?.description || ""],
      descriptionEN: [translationEN?.description || ""],
      yearCS: [translationCS?.year || ""],
      yearEN: [translationEN?.year || ""],
      year: [this.publication.year],
      curated: [this.publication.curated || false],
    });

    this.toggleDisabledOriginalTitle(
      this.form.get("originalTitleLocale").value
    );

    this.addModeBasedControls();
    this.subscribeToOriginalTitleLocale();
    this.subscribeToTitles();
  }

  private addModeBasedControls() {
    if (this.isCreateMode()) {
    }
    if (this.isUpdateMode()) {
    }
  }

  private subscribeToOriginalTitleLocale() {
    this.originalTitleLocaleSubscription = this.form
      .get("originalTitleLocale")
      .valueChanges.subscribe((value) => {
        this.toggleDisabledOriginalTitle(value);
      });
  }

  private toggleDisabledOriginalTitle(originalTitleLocaleValue: string) {
    if (originalTitleLocaleValue === "other") {
      this.form.get("originalTitle").enable();
    } else {
      this.form.get("originalTitle").disable();
    }
    this.setOriginalTitle();
    this.toggleRequiredTitle(originalTitleLocaleValue);
  }

  private subscribeToTitles() {
    this.titleEnSubscription = this.form
      .get("titleEN")
      .valueChanges.subscribe(() => {
        this.setOriginalTitle();
      });
    this.titleCsSubscription = this.form
      .get("titleCS")
      .valueChanges.subscribe(() => {
        this.setOriginalTitle();
      });
  }

  private setOriginalTitle() {
    switch (this.form.get("originalTitleLocale").value) {
      case "cs":
        this.form.get("originalTitle").setValue(this.form.get("titleCS").value);
        break;
      case "en":
        this.form.get("originalTitle").setValue(this.form.get("titleEN").value);
        break;
      case "other":
        this.form
          .get("originalTitle")
          .setValue(this.publication.originalTitle || "");
        break;
      default:
        break;
    }
  }

  private toggleRequiredTitle(originalTitleLocaleValue: string) {
    this.TITLE_CONTROL_NAMES.map((title) =>
      this.form.get(title).clearValidators()
    );
    switch (originalTitleLocaleValue) {
      case "cs":
        this.form.get("titleCS").setValidators(Validators.required);

        break;
      case "en":
        this.form.get("titleEN").setValidators(Validators.required);

        break;
      case "other":
        this.form.get("originalTitle").setValidators(Validators.required);

        break;
      default:
        break;
    }

    this.TITLE_CONTROL_NAMES.map((title) =>
      this.form.get(title).updateValueAndValidity()
    );
  }

  private getLocalizedOriginalTitle(): string {
    let originalTitle = this.form.value["originalTitle"];
    switch (this.form.value["originalTitleLocale"]) {
      case "cs":
        originalTitle = this.form.value["titleCS"];
        break;
      case "en":
        originalTitle = this.form.value["titleEN"];
        break;
      default:
        break;
    }
    return originalTitle;
  }

  getLocaleByCode(code: string): AvailableLocale {
    return this.availableLocales.find((locale) => locale.code === code);
  }
}
